import { memo } from "react"
import { FieldArray, useFormikContext } from "formik"
import { useSelector } from "react-redux"
import CustomTable from "../../../investorOverview/common/customTable"
import { TableHeaderData } from "../../../investorOverview/types"
import { Box, IconButton, TableCell, TableRow, Theme, Typography, useTheme } from "@mui/material"
import { MFTextField } from "../../../../lib/formik"
import { allowDecimalNumbers } from "../../../../utils/utilityFunctions"
import { BluePlusIcon, DeleteIcons } from "../../../../assets/SvgIcons"
import { AuaFeeSlabWiseTableDataObj, AuaFeeVariableTableDataObj, FEE_TYPE_VALUES, FeeTypeTableDataMap, FeeTypeValue, FixedFeeTableDataObj, SlabsObj, TableFormValues } from "."
import { RootStateType } from "../../../../redux-store/reducers"

function getTableHeaderData(feeType: FeeTypeValue | string): Array<TableHeaderData> {
    return (
        [
            {
                id: 'clientCode',
                label: 'Investor Code',
                align: 'left',
                isSortable: false,
            },
            {
                id: 'name',
                label: 'Name',
                align: 'left',
                isSortable: false,
            },
            {
                id: 'status',
                label: 'Investor Type',
                align: 'center',
                isSortable: false,
            },
            {
                id: 'investorType',
                label: 'Dependency Status',
                align: 'center',
                isSortable: false,
            },
            {
                id: 'invoiceStatus',
                label: 'Status',
                align: 'center',
                isSortable: false,
            },
            ...(feeType === FEE_TYPE_VALUES.FIXED_FEE ? [{
                id: 'fixed_rate',
                label: 'Annual Fixed Fee\nAmount (₹) *',
                align: 'center',
                isSortable: false,
                headerTableCellStyle: {
                    whiteSpace: 'pre-line',
                }
            }] as Array<TableHeaderData> : []),
            ...(feeType === FEE_TYPE_VALUES.AUA_FEE_VARIABLE ? [
                {
                    id: 'variable_rate',
                    label: 'Fee percentage\nP.A (%) *',
                    align: 'center',
                    isSortable: false,
                    headerTableCellStyle: {
                        whiteSpace: 'pre-line',
                    }
                }
            ] as Array<TableHeaderData> : []),
            ...(feeType === FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE ? [
                {
                    id: 'rate',
                    label: 'Fee percentage\nP.A (%) *',
                    align: 'center',
                    isSortable: false,
                    headerTableCellStyle: {
                        whiteSpace: 'pre-line',
                    }
                },
                {
                    id: 'slab_min',
                    label: 'Min Aum (₹) *',
                    align: 'center',
                    isSortable: false,
                },
                {
                    id: 'slab_max',
                    label: 'Max Aum (₹) *',
                    align: 'center',
                    isSortable: false,
                }
            ] as Array<TableHeaderData> : []),
            {
                id: 'contactNo',
                label: 'Phone Number',
                align: 'center',
                isSortable: false,
            },
            {
                id: 'email',
                label: 'Email',
                align: 'center',
                isSortable: false,
            },
        ]
    )
}

function HofBadge(): JSX.Element {
    const theme = useTheme()

    return (
        <Box
            component="span"
            sx={{
                padding: "2px 8px",
                borderRadius: "10px",
                backgroundColor: theme.palette.grey[700]
            }}
        >
            <Typography
                variant="subtitle_500"
                sx={{ color: theme.palette.text.primary }}
            >
                HOF
            </Typography>
        </Box>
    )
}

const INVESTOR_TYPE_COLOR_MAP: Record<string, string> = {
    independent: "success.500",
    dependent: "error.500",
    "non-individual": "warning.500"
}

const STATUS_COLOR_MAP: Record<string, string> = {
    freeze: "primary.500",
    freezed: "primary.500",
    unfreeze: "primary.200",
    unfreezed: "primary.200"
}

const ROW_HEIGHT = 95
const VALIDATION_MESSAGE_SPACE = 28

const getRowStyles = (theme: Theme) => ({
    "& td": {
        paddingTop: "10px",
        paddingBottom: `${10 + VALIDATION_MESSAGE_SPACE}px`,
        borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
        verticalAlign: "middle",
        minHeight: ROW_HEIGHT,
    }
})

const getSlabStyles = {
    "& td": {
        paddingTop: "8px",
        paddingBottom: `${8 + VALIDATION_MESSAGE_SPACE}px`,
        borderBottom: 0,
        verticalAlign: "middle",
        minHeight: ROW_HEIGHT,
    }
}

const INPUT_TEXT_FIELD_HEIGHT = 45

const getTextFieldStyles = ({
    theme,
    width,
    disabled,
    autoHeight = false,
}: {
    theme: Theme;
    width: number | string;
    disabled: boolean;
    autoHeight?: boolean;
}) => ({
    mt: 0,
    mb: 0,
    width,
    maxWidth: width,
    position: "relative",
    "& .MuiOutlinedInput-root": {
        ...(autoHeight
            ? { minHeight: INPUT_TEXT_FIELD_HEIGHT, height: "auto" }
            : { height: INPUT_TEXT_FIELD_HEIGHT }),
        borderRadius: "10px",
        backgroundColor: disabled
            ? `${theme.palette.text.secondary}1A`
            : theme.palette.common.white,
        ...(autoHeight && {
            alignItems: "center",
            padding: 0,
        }),
    },
    "& .MuiOutlinedInput-input": {
        padding: "8px 12px",
        ...(autoHeight && {
            wordBreak: "break-all",
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
        }),
    },
    "& .Mui-disabled .MuiInputBase-input, & .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: theme.palette.grey[500],
    },
    "& .MuiFormHelperText-root": {
        position: "absolute",
        top: "100%",
        left: 0,
        margin: 0,
        marginTop: "6px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: "14px",
        maxWidth: "100%",
    },
})

const FixedFeeTableBody = memo(function FixedFeeTableBody({
    tableData,
    isDisabled
}: {
    tableData: Array<FixedFeeTableDataObj>;
    isDisabled: boolean;
}): JSX.Element {
    const theme = useTheme()

    return (
        <>
            {tableData.map((rowData: FixedFeeTableDataObj, index: number) => {
                const isFreezed = ["freezed", "freeze"].includes(`${rowData?.invoiceStatus ?? ""}`.toLowerCase())

                return (
                    <TableRow
                        key={rowData?.clientCode}
                        sx={getRowStyles}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.text.primary }}
                            >
                                {rowData?.clientCode ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="left">
                            <Box sx={{ display: "flex", alignItems: "center", columnGap: "8px" }}>
                                <Typography
                                    variant="body_500"
                                    sx={{ color: theme.palette.primary[500] }}
                                >
                                    {rowData?.name ?? "-"}
                                </Typography>
                                {["y"].includes(`${rowData?.isHof ?? ""}`.toLowerCase()) && <HofBadge />}
                            </Box>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.primary[500] }}
                            >
                                {rowData?.status ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{ color: INVESTOR_TYPE_COLOR_MAP[`${rowData?.investorType ?? ""}`.toLowerCase()] ?? theme.palette.text.primary }}
                            >
                                {rowData?.investorType ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{ color: STATUS_COLOR_MAP[`${rowData?.invoiceStatus ?? ""}`.toLowerCase()] ?? theme.palette.text.primary }}
                            >
                                {rowData?.invoiceStatus ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <MFTextField
                                name={`fixedFeeTableData.${index}.fixed_rate`}
                                applyMarginBottom={false}
                                onKeyDown={allowDecimalNumbers}
                                sx={getTextFieldStyles({ theme, width: 150, disabled: (isFreezed || isDisabled), autoHeight: true })}
                                formatCurrency
                                multiline
                                disabled={isFreezed || isDisabled}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {rowData?.contactNo ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {rowData?.email ?? "-"}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
})

const AuaFeeVariableTableBody = memo(function AuaFeeVariableTableBody({
    tableData,
    isDisabled
}: {
    tableData: Array<AuaFeeVariableTableDataObj>;
    isDisabled: boolean;
}): JSX.Element {
    const theme = useTheme()

    return (
        <>
            {tableData.map((rowData: AuaFeeVariableTableDataObj, index: number) => {
                const isFreezed = ["freezed", "freeze"].includes(`${rowData?.invoiceStatus ?? ""}`.toLowerCase())

                return (
                    <TableRow
                        key={rowData?.clientCode}
                        sx={getRowStyles}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.text.primary }}
                            >
                                {rowData?.clientCode ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="left">
                            <Box sx={{ display: "flex", alignItems: "center", columnGap: "8px" }}>
                                <Typography
                                    variant="body_500"
                                    sx={{ color: theme.palette.primary[500] }}
                                >
                                    {rowData?.name ?? "-"}
                                </Typography>
                                {["y"].includes(`${rowData?.isHof ?? ""}`.toLowerCase()) && <HofBadge />}
                            </Box>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.primary[500] }}
                            >
                                {rowData?.status ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{ color: INVESTOR_TYPE_COLOR_MAP[`${rowData?.investorType ?? ""}`.toLowerCase()] ?? theme.palette.text.primary }}
                            >
                                {rowData?.investorType ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{ color: STATUS_COLOR_MAP[`${rowData?.invoiceStatus ?? ""}`.toLowerCase()] ?? theme.palette.text.primary }}
                            >
                                {rowData?.invoiceStatus ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <MFTextField
                                name={`auaFeeVariableTableData.${index}.variable_rate`}
                                applyMarginBottom={false}
                                onKeyDown={allowDecimalNumbers}
                                sx={getTextFieldStyles({ theme, width: 150, disabled: (isFreezed || isDisabled) })}
                                disabled={isFreezed || isDisabled}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {rowData?.contactNo ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {rowData?.email ?? "-"}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
})

const AuaFeeSlabWiseTableBody = memo(function AuaFeeSlabWiseTableBody({
    tableData,
    isDisabled
}: {
    tableData: Array<AuaFeeSlabWiseTableDataObj>;
    isDisabled: boolean;
}): JSX.Element {
    const theme = useTheme()

    // Data from Redux
    const { locale } = useSelector((store: RootStateType) => store.currencyInfo);

    const { setFieldTouched, validateForm } = useFormikContext<TableFormValues>()

    const handleAddSlab = async (
        rowIndex: number,
        currentSlabsData: Array<SlabsObj>,
        helpers: { push: (obj: SlabsObj) => void }
    ) => {
        currentSlabsData.forEach((_, slabIndex) => {
            setFieldTouched(`auaFeeSlabWiseTableData.${rowIndex}.slabs.${slabIndex}.rate`, true, false)
            setFieldTouched(`auaFeeSlabWiseTableData.${rowIndex}.slabs.${slabIndex}.slab_max`, true, false)
        })

        const validationErrors = await validateForm()

        const rowErrors = (validationErrors as Record<string, unknown>)?.auaFeeSlabWiseTableData as Array<{ slabs?: unknown }> | undefined
        const slabErrors = rowErrors?.[rowIndex]?.slabs

        if (slabErrors) {
            if (Array.isArray(slabErrors)) {
                const hasSlabErrors = slabErrors.some((slabError) => slabError != null)
                if (hasSlabErrors) return
            } else {
                return
            }
        }

        const previousSlab = currentSlabsData[currentSlabsData.length - 1]
        const previousSlabMaxAumValue = previousSlab?.slab_max ? Number(`${previousSlab?.slab_max ?? ""}`.replaceAll(',', '')) : NaN
        const newSlabMinAumValue = !Number.isNaN(previousSlabMaxAumValue) ? Number(previousSlabMaxAumValue + 1).toLocaleString(locale) : ""

        helpers.push({
            rate: "",
            slab_min: newSlabMinAumValue,
            slab_max: "",
        })
    }

    return (
        <>
            {tableData.map((rowData: AuaFeeSlabWiseTableDataObj, rowIndex: number) => {
                const isFreezed = ["freezed", "freeze"].includes(`${rowData?.invoiceStatus ?? ""}`.toLowerCase())
                const currentSlabsData = rowData?.slabs ?? []

                return (
                    <FieldArray
                        key={`${rowData.clientCode}-${rowIndex}`}
                        name={`auaFeeSlabWiseTableData.${rowIndex}.slabs`}
                    >
                        {(helpers) => (
                            <>
                                {currentSlabsData.map((slab: SlabsObj, slabIndex: number) => {
                                    const isFirstSlab = (slabIndex === 0)
                                    const isLastSlab = (slabIndex === (currentSlabsData.length - 1))

                                    return (
                                        <TableRow
                                            key={`${rowData.clientCode}-${rowIndex}-${slabIndex}`}
                                            sx={isLastSlab ? getRowStyles : getSlabStyles}
                                        >
                                            {isFirstSlab ? (
                                                <>
                                                    <TableCell align="left">
                                                        <Typography
                                                            variant="body_500"
                                                            sx={{ color: theme.palette.text.primary }}
                                                        >
                                                            {rowData?.clientCode ?? "-"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        <Box sx={{ display: "flex", alignItems: "center", columnGap: "8px" }}>
                                                            <Typography
                                                                variant="body_500"
                                                                sx={{ color: theme.palette.primary[500] }}
                                                            >
                                                                {rowData?.name ?? "-"}
                                                            </Typography>
                                                            {["y"].includes(`${rowData?.isHof ?? ""}`.toLowerCase()) && <HofBadge />}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body_500"
                                                            sx={{ color: theme.palette.primary[500] }}
                                                        >
                                                            {rowData?.status ?? "-"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body_500"
                                                            sx={{ color: INVESTOR_TYPE_COLOR_MAP[`${rowData?.investorType ?? ""}`.toLowerCase()] ?? theme.palette.text.primary }}
                                                        >
                                                            {rowData?.investorType ?? "-"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body_500"
                                                            sx={{ color: STATUS_COLOR_MAP[`${rowData?.invoiceStatus ?? ""}`.toLowerCase()] ?? theme.palette.text.primary }}
                                                        >
                                                            {rowData?.invoiceStatus ?? "-"}
                                                        </Typography>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell />
                                                    <TableCell />
                                                    <TableCell />
                                                    <TableCell />
                                                    <TableCell />
                                                </>
                                            )}
                                            <TableCell align="center">
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "flex-start",
                                                        columnGap: "8px",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            height: INPUT_TEXT_FIELD_HEIGHT,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {isFirstSlab ? (
                                                            <IconButton
                                                                size="small"
                                                                disabled={isFreezed || isDisabled}
                                                                onClick={() => handleAddSlab(rowIndex, currentSlabsData, helpers)}
                                                                sx={{
                                                                    padding: "4px",
                                                                    "&.Mui-disabled svg": {
                                                                        opacity: 0.38,
                                                                    }
                                                                }}
                                                            >
                                                                <BluePlusIcon />
                                                            </IconButton>
                                                        ) : (
                                                            <IconButton
                                                                size="small"
                                                                disabled={isFreezed || isDisabled}
                                                                onClick={() => helpers.remove(slabIndex)}
                                                                sx={{
                                                                    padding: "4px",
                                                                    backgroundColor: `${theme.palette.error[500]}1A`,
                                                                    borderRadius: "8px",
                                                                    "&:hover": {
                                                                        backgroundColor: `${theme.palette.error[500]}33`,
                                                                    },
                                                                    "&.Mui-disabled svg": {
                                                                        opacity: 0.38,
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcons width={16} height={16} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                    <MFTextField
                                                        name={`auaFeeSlabWiseTableData.${rowIndex}.slabs.${slabIndex}.rate`}
                                                        applyMarginBottom={false}
                                                        onKeyDown={allowDecimalNumbers}
                                                        sx={getTextFieldStyles({ theme, width: 150, disabled: (isFreezed || isDisabled) })}
                                                        disabled={isFreezed || isDisabled}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <MFTextField
                                                    name={`auaFeeSlabWiseTableData.${rowIndex}.slabs.${slabIndex}.slab_min`}
                                                    applyMarginBottom={false}
                                                    onKeyDown={allowDecimalNumbers}
                                                    sx={getTextFieldStyles({ theme, width: 150, disabled: true, autoHeight: true })}
                                                    formatCurrency
                                                    multiline
                                                    // disabled={isFreezed || isDisabled}
                                                    disabled
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <MFTextField
                                                    name={`auaFeeSlabWiseTableData.${rowIndex}.slabs.${slabIndex}.slab_max`}
                                                    applyMarginBottom={false}
                                                    onKeyDown={allowDecimalNumbers}
                                                    sx={getTextFieldStyles({ theme, width: 150, disabled: (isFreezed || isDisabled || !isLastSlab), autoHeight: true })}
                                                    formatCurrency
                                                    multiline
                                                    disabled={isFreezed || isDisabled || !isLastSlab}
                                                />
                                            </TableCell>
                                            {isFirstSlab ? (
                                                <>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body_500"
                                                            sx={{
                                                                lineHeight: "20px",
                                                                letterSpacing: "0.02px",
                                                                color: theme.palette.text.primary,
                                                            }}
                                                        >
                                                            {rowData?.contactNo ?? "-"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body_500"
                                                            sx={{
                                                                lineHeight: "20px",
                                                                letterSpacing: "0.02px",
                                                                color: theme.palette.text.primary,
                                                            }}
                                                        >
                                                            {rowData?.email ?? "-"}
                                                        </Typography>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell />
                                                    <TableCell />
                                                </>
                                            )}

                                        </TableRow>
                                    )
                                })}
                            </>
                        )}
                    </FieldArray>
                )
            })}
        </>
    )
})


type TableBodyInput =
    | {
        feeType: typeof FEE_TYPE_VALUES.FIXED_FEE;
        tableData: FeeTypeTableDataMap[typeof FEE_TYPE_VALUES.FIXED_FEE];
        isDisabled: boolean;
    }
    | {
        feeType: typeof FEE_TYPE_VALUES.AUA_FEE_VARIABLE;
        tableData: FeeTypeTableDataMap[typeof FEE_TYPE_VALUES.AUA_FEE_VARIABLE];
        isDisabled: boolean;
    }
    | {
        feeType: typeof FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE;
        tableData: FeeTypeTableDataMap[typeof FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE];
        isDisabled: boolean;
    }

function getTableBody({ feeType, tableData, isDisabled }: TableBodyInput): JSX.Element {
    switch (feeType) {
        case FEE_TYPE_VALUES.FIXED_FEE:
            return <FixedFeeTableBody tableData={tableData} isDisabled={isDisabled} />
        case FEE_TYPE_VALUES.AUA_FEE_VARIABLE:
            return <AuaFeeVariableTableBody tableData={tableData} isDisabled={isDisabled} />
        case FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE:
            return <AuaFeeSlabWiseTableBody tableData={tableData} isDisabled={isDisabled} />
        default:
            return <></>
    }
}

export default function FamilyMemberDetailsTable({
    isLoading,
    isDisabled
}: {
    isLoading: boolean;
    isDisabled: boolean;
}): JSX.Element {
    const { values } = useFormikContext<TableFormValues>()

    const tableDataMap: FeeTypeTableDataMap = {
        [FEE_TYPE_VALUES.FIXED_FEE]: values?.fixedFeeTableData ?? [],
        [FEE_TYPE_VALUES.AUA_FEE_VARIABLE]: values?.auaFeeVariableTableData ?? [],
        [FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE]: values?.auaFeeSlabWiseTableData ?? []
    }

    const feeType = (values?.feeType ?? "") as FeeTypeValue
    const tableData = tableDataMap[feeType] ?? []

    return (
        <CustomTable
            isLoading={isLoading}
            tableBody={getTableBody({ feeType, tableData, isDisabled } as TableBodyInput)}
            tableData={tableData}
            tableHeaderData={getTableHeaderData(feeType)}
            tableHeadRowCustomStyles={{
                '& th:first-of-type': {
                    paddingLeft: "15px"
                }
            }}
            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0
                }
            }}
            loadingRowsNum={7}
            tableDataCount={(tableData ?? []).length}
            isPaginationVisible={false}
        />
    )
}